import axiosClient from '@/api/axiosClient'
import {
  IBankConnection,
  IBankConnectionDetail,
  IBankInstitution,
  IFinancialAccount,
  ISyncHistory,
} from '@/types'

export const BankService = {
  // Not customer-scoped: the supported-institution list is global, so this is
  // the one call here that does not read the selected user. VITE_API_BASE_URL
  // already ends in /support, so the full path is /support/bank-institutions.
  fetchBankInstitutions: async (): Promise<IBankInstitution[]> => {
    const response = await axiosClient.get('/bank-institutions')
    return response.data.data
  },

  // Moves one institution in the list shown to customers. Global like the list itself. The gateway
  // binds the body straight to the request data, so it is sent unwrapped; the reply carries the
  // institution as stored.
  updateInstitutionDisplayOrder: async (
    institutionId: string,
    displayOrder: number
  ): Promise<IBankInstitution> => {
    const response = await axiosClient.post(`/bank-institutions/${institutionId}:rearrange-order`, {
      displayOrder,
    })
    return response.data.data.institution
  },

  // The connection reads take the customer rather than reading the selected one from IndexedDB,
  // which every tab shares: the caller passes the customer its own tab is showing.
  fetchConnections: async (userId: string): Promise<IBankConnection[]> => {
    const response = await axiosClient.get(`/users/${userId}/bank-connections`)
    return response.data.data
  },

  fetchConnectionDetail: async (
    userId: string,
    connectionId: string
  ): Promise<IBankConnectionDetail> => {
    const response = await axiosClient.get(`/users/${userId}/bank-connections/${connectionId}`)
    return response.data
  },
  // Removes the connection and the accounts under it.
  deleteConnection: async (userId: string, connectionId: string): Promise<void> => {
    await axiosClient.delete(`/users/${userId}/bank-connections/${connectionId}`)
  },
  fetchSyncHistory: async (userId: string, connectionId: string): Promise<ISyncHistory[]> => {
    const response = await axiosClient.get(
      `/users/${userId}/bank-connections/${connectionId}/history`
    )
    return response.data.data
  },
  // The values a transaction filter can offer, read from the customer's connections and their
  // accounts rather than from whichever transactions happen to have been loaded. The backend masks
  // an account number here with the same setting it uses on a transaction, so the numbers line up.
  //
  // A connection whose accounts cannot be read (a broken or expired one) is skipped rather than
  // failing the lot: partial reference data still beats none.
  fetchConnectionsWithAccounts: async (
    userId: string
  ): Promise<{
    connections: IBankConnection[]
    accounts: IFinancialAccount[]
  }> => {
    const connections = await BankService.fetchConnections(userId)

    const results = await Promise.allSettled(
      connections.map(connection => BankService.fetchFinancialAccounts(userId, connection.id))
    )

    const accounts = results.flatMap(result => (result.status === 'fulfilled' ? result.value : []))

    return { connections, accounts }
  },
  fetchFinancialAccounts: async (
    userId: string,
    connectionId: string
  ): Promise<IFinancialAccount[]> => {
    const response = await axiosClient.get(
      `/users/${userId}/bank-connections/${connectionId}/accounts`
    )
    return response.data.data
  },
  // Sets the description alias on up to BULK_ALIAS_BATCH_SIZE transactions, all on one account.
  // The backend applies them in order and stops at the first failure, keeping the ones before it;
  // setting an alias is idempotent, so a failed batch can be sent again as is.
  //
  // Takes the customer rather than reading the selected one: an update runs as several requests,
  // and the operator can pick another customer part way through.
  bulkUpdateDescriptionAlias: async (
    userId: string,
    accountId: string,
    data: { transactionId: string; descriptionAlias: string }[]
  ): Promise<void> => {
    await axiosClient.post(
      `/users/${userId}/accounts/${accountId}/transactions:bulk-update-description-alias`,
      { data }
    )
  },
}

export const BULK_ALIAS_BATCH_SIZE = 100
